import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface ThermometerVisualProps {
  minTemp?: number;
  maxTemp?: number;
  currentTemp: number;
  targetTemp?: number;
  unit?: 'C' | 'F';
  label?: string;
  mode?: 'stimulus' | 'support';
}
export function ThermometerVisual({
  minTemp = -20,
  maxTemp = 50,
  currentTemp,
  targetTemp,
  unit = 'C',
  label,
  mode = 'support'
}: ThermometerVisualProps) {
  const computed = useMemo(() => {
    const range = maxTemp - minTemp;
    const currentPercent = (currentTemp - minTemp) / range * 100;
    const targetPercent = targetTemp !== undefined ? (targetTemp - minTemp) / range * 100 : null;
    // Calculate tick marks — use smaller intervals for narrow ranges
    const tickInterval = range <= 40 ? 5 : 10;
    const ticks: number[] = [];
    for (let temp = Math.ceil(minTemp / tickInterval) * tickInterval; temp <= maxTemp; temp += tickInterval) {
      ticks.push(temp);
    }
    return {
      range,
      currentPercent: Math.max(0, Math.min(100, currentPercent)),
      targetPercent: targetPercent !== null ? Math.max(0, Math.min(100, targetPercent)) : null,
      ticks,
      tickInterval
    };
  }, [minTemp, maxTemp, currentTemp, targetTemp]);
  // Compact SVG dimensions for mobile
  const tubeTop = 8;
  const tubeHeight = 100;
  const tubeBottom = tubeTop + tubeHeight;
  const bulbCy = tubeBottom + 10;
  const svgHeight = bulbCy + 16;
  const thermometerViz = <div className="flex justify-center items-center" data-id="element-9162">
      <svg width="80" height={svgHeight} viewBox={`0 0 80 ${svgHeight}`} className="drop-shadow-sm" data-id="element-9163">
        {/* Thermometer tube */}
        <rect x="30" y={tubeTop} width="16" height={tubeHeight} rx="8" fill="white" stroke="#cbd5e1" strokeWidth="1.5" data-id="element-9164" />

        {/* Scale markings */}
        {computed.ticks.map(temp => {
        const percent = (temp - minTemp) / computed.range * 100;
        const y = tubeBottom - percent / 100 * (tubeHeight - 6);
        const isMajor = temp % (computed.tickInterval * 2) === 0 || temp === 0;
        return <g key={temp} data-id="element-9165">
              <line x1={isMajor ? '21' : '25'} y1={y} x2="30" y2={y} stroke="#94a3b8" strokeWidth={isMajor ? '1.5' : '0.75'} data-id="element-9166" />
              {isMajor && <text x="18" y={y + 3} textAnchor="end" fontSize="8" fill="#64748b" fontWeight="600" data-id="element-9167">
                  {temp}°
                </text>}
            </g>;
      })}

        {/* Mercury column (animated) */}
        <motion.rect x="33" y={tubeBottom - computed.currentPercent / 100 * (tubeHeight - 6)} width="10" height={computed.currentPercent / 100 * (tubeHeight - 6) + 12} rx="5" fill="#ef4444" initial={{
        height: 0,
        y: tubeBottom + 6
      }} animate={{
        height: computed.currentPercent / 100 * (tubeHeight - 6) + 12,
        y: tubeBottom - computed.currentPercent / 100 * (tubeHeight - 6)
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-9168" />

        {/* Bulb at bottom */}
        <circle cx="38" cy={bulbCy} r="12" fill="#dc2626" stroke="#b91c1c" strokeWidth="1.5" data-id="element-9169" />
        <circle cx="38" cy={bulbCy} r="7" fill="#ef4444" data-id="element-9170" />

        {/* Target marker (if provided) */}
        {computed.targetPercent !== null && mode === 'stimulus' && <g data-id="element-9171">
            <line x1="46" y1={tubeBottom - computed.targetPercent / 100 * (tubeHeight - 6)} x2="58" y2={tubeBottom - computed.targetPercent / 100 * (tubeHeight - 6)} stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="3 2" data-id="element-9172" />
            <text x="62" y={tubeBottom - computed.targetPercent / 100 * (tubeHeight - 6) + 3} fontSize="10" fill="#d97706" fontWeight="bold" data-id="element-9173">
              ?
            </text>
          </g>}

        {/* Current temperature label */}
        {mode === 'support' && <text x="62" y={tubeBottom - computed.currentPercent / 100 * (tubeHeight - 6) + 3} fontSize="10" fill="#dc2626" fontWeight="bold" data-id="element-9174">
            {currentTemp}°
          </text>}
      </svg>
    </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-3 border border-slate-200 w-full max-w-[12rem] mx-auto" data-id="element-9175">
        {label && <p className="text-xs font-semibold text-slate-700 mb-1.5 text-center" data-id="element-9176">
            {label}
          </p>}
        {thermometerViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-rose-100 overflow-hidden w-full max-w-[14rem] mx-auto shadow-sm" data-id="element-9177">
      <div className="bg-rose-50 px-3 py-2 border-b border-rose-100" data-id="element-9178">
        <h3 className="text-xs font-bold text-rose-900 text-center" data-id="element-9179">
          {label || 'Thermometer'}
        </h3>
      </div>

      <div className="p-3 bg-white" data-id="element-9180">{thermometerViz}</div>

      <div className="bg-rose-50/50 px-3 py-2 border-t border-rose-50" data-id="element-9181">
        <p className="text-xs text-slate-700 text-center leading-relaxed" data-id="element-9182">
          De thermometer geeft{' '}
          <strong data-id="element-9183">
            {currentTemp}°{unit}
          </strong>{' '}
          aan.
          {currentTemp < 0 ? ' Dit is onder het vriespunt.' : ' Kijk naar de bovenkant van de rode vloeistof.'}
        </p>
      </div>
    </div>;
}